/**
 * SkillsSection
 *
 * Renders the citizen's skills block: current role, languages.
 * Read-only when `onEdit` is omitted.
 */

import { useTranslation } from '@tonnie/locale';
import { Code, Briefcase, Languages } from '@tonnie/assets';
import { Badge } from '@tonnie/ui';
import { ProfileSectionCard } from '../ProfileSectionCard';
import { IOnboardingData, ProfileReviewEditHandler } from '../types';

interface ISkillsSectionProps {
  data: NonNullable<IOnboardingData['skills']>;
  onEdit?: ProfileReviewEditHandler;
}

export function SkillsSection({ data, onEdit }: ISkillsSectionProps) {
  const { t } = useTranslation();

  return (
    <ProfileSectionCard
      icon={Code}
      title={t('profile.skills')}
      description={t('profile.skillsDescription')}
      stepId="skills"
      onEdit={onEdit}
    >
      {data.role && (
        <div className="space-y-1.5">
          <p className="text-xs font-medium text-muted-foreground uppercase tracking-wide flex items-center gap-1.5">
            <Briefcase className="w-3 h-3" />
            {t('profile.currentRole')}
          </p>
          <p className="text-base font-semibold text-foreground">{data.role}</p>
        </div>
      )}

      {data.languages && data.languages.length > 0 && (
        <div className="space-y-2 pt-2 border-t border-border/50">
          <p className="text-xs font-medium text-muted-foreground uppercase tracking-wide flex items-center gap-1.5">
            <Languages className="w-3 h-3" />
            {t('profile.languages')}
          </p>
          <div className="flex flex-wrap gap-2">
            {data.languages.map((lang, idx) => (
              <Badge key={idx} variant="secondary" className="text-xs font-medium px-2.5 py-1">
                {lang.name} <span className="ml-1.5 text-muted-foreground">({lang.level})</span>
              </Badge>
            ))}
          </div>
        </div>
      )}
    </ProfileSectionCard>
  );
}
